import * as React from "react";
import {
  AlertOctagon,
  CalendarClock,
  CheckCircle2,
  FileWarning,
  HelpCircle,
  ShieldCheck,
} from "lucide-react";
import type { TraegerChildSummary } from "./traeger-api";

/**
 * child-status.tsx — B11: zwei ORTHOGONALE status-dimensionen pro
 * Einrichtung, visuell klar getrennt.
 *
 * PROBLEM (B11): Im Träger-Oversight sahen eine lizenz-GESPERRTE Einrichtung
 * und eine mit echter DOKU-LÜCKE beide gleich „rot/unvollständig" aus — der
 * Träger-Admin (Birgit) konnte „Lizenz abgelaufen, bitte verlängern" nicht von
 * „Lizenz ok, aber Doku unvollständig, bitte mit Einrichtungs-Admin klären"
 * unterscheiden. Zwei völlig verschiedene handlungen, ein roter chip.
 *
 * LÖSUNG: Pro Einrichtung werden ZWEI getrennte status-chips gerendert:
 *   1. Lizenz-Status   (aktiv / abgelaufen / unbekannt)
 *   2. Doku-Status     (vollständig / Hinweis / Lücke)
 * Niemals zu einem vermischten „rot" kollabiert.
 *
 * color-blind-safe: jeder status trägt Farbe IMMER zusammen mit einem
 * eindeutigen Icon UND klartext-label (dreifach-kodierung). Die farb-tokens
 * stammen aus tokens.css (heat-*-soft + heat-* / cool-teal), alle WCAG-AA
 * gegen ihren text verifiziert (siehe tokens.css).
 *
 * ── DATEN-REALITÄT (wichtig) ────────────────────────────────────────────────
 * `compliance_status` (green/yellow/red) aus dem backend ist FAKTISCH ein
 * reiner DOKU-status — `computeCompliance` in api/.../traeger.go bewertet nur
 * doku-dichte vs. hitze-stufen, NICHT die lizenz. Es gibt heute KEIN
 * lizenz-status-feld pro child-org im /v1/traeger/dashboard-response
 * (TraegerChildSummary). `license.ts` kennt nur die EIGENE träger-lizenz
 * (JWT-claim lic_exp), nicht die je-einrichtung.
 *
 * Deshalb liest die lizenz-dimension das optionale, additive feld
 * `license_status` (siehe traeger-api.ts) — solange das backend es nicht
 * liefert, ist es `undefined` → wir zeigen ehrlich „Lizenz-Status unbekannt"
 * statt ein falsches „aktiv/grün" vorzutäuschen. → BE-followup in notes[].
 */

// ─── Lizenz-dimension ────────────────────────────────────────────────────────

export type ChildLicenseState = "active" | "expired" | "unknown";

interface StatusVisual {
  label: string;
  /** kurz-aria-zusatz, hängt sich an label für screen-reader */
  srHint: string;
  /** lucide-icon — dritte (form-)kodierung neben farbe + text */
  Icon: typeof CheckCircle2;
  /** tailwind classes: bg + text + border, alle aus design-tokens */
  classes: string;
}

const LICENSE_VISUAL: Record<ChildLicenseState, StatusVisual> = {
  active: {
    label: "Lizenz aktiv",
    srHint: "Lizenz aktiv",
    Icon: ShieldCheck,
    classes:
      "bg-[color:var(--color-cool-teal-50)] text-[color:var(--color-cool-teal-700)] border-[color:var(--color-cool-teal-600)]",
  },
  expired: {
    label: "Lizenz abgelaufen",
    srHint: "Lizenz abgelaufen — gesperrt für neue Einträge",
    Icon: AlertOctagon,
    classes:
      "bg-[color:var(--color-heat-4-soft)] text-[color:var(--color-heat-4)] border-[color:var(--color-heat-4)]",
  },
  unknown: {
    label: "Lizenz: Status unbekannt",
    srHint: "Lizenz-Status liegt nicht vor",
    Icon: HelpCircle,
    classes:
      "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-secondary)] border-[color:var(--color-border-strong)]",
  },
};

/**
 * Leitet die lizenz-dimension aus dem (optionalen) backend-feld ab.
 * Fehlt das feld → "unknown" (NICHT raten). Die enforcement bleibt
 * server-seitig (middleware RequireActiveLicense, 403 license_expired);
 * dieser chip ist reine anzeige, kein gate.
 */
export function childLicenseState(child: TraegerChildSummary): ChildLicenseState {
  if (child.license_status === "expired") return "expired";
  if (child.license_status === "active") return "active";
  return "unknown";
}

// ─── Doku-dimension ──────────────────────────────────────────────────────────

export type ChildDocState = "complete" | "hint" | "gap";

const DOC_VISUAL: Record<ChildDocState, StatusVisual> = {
  complete: {
    label: "Doku vollständig",
    srHint: "Dokumentation vollständig",
    Icon: CheckCircle2,
    classes:
      "bg-[color:var(--color-heat-1-soft)] text-[color:var(--color-heat-1)] border-[color:var(--color-heat-1)]",
  },
  hint: {
    label: "Doku-Hinweis",
    srHint: "Dokumentation lückenhaft — Hinweis",
    Icon: FileWarning,
    classes:
      "bg-[color:var(--color-heat-2-soft)] text-[color:var(--color-heat-2)] border-[color:var(--color-heat-2)]",
  },
  gap: {
    label: "Doku-Lücke",
    srHint: "Dokumentations-Lücke trotz kritischer Hitze",
    Icon: FileWarning,
    classes:
      "bg-[color:var(--color-heat-3-soft)] text-[color:var(--color-heat-3)] border-[color:var(--color-heat-3)]",
  },
};

/**
 * Mappt den backend-`compliance_status` (faktisch ein doku-status) auf die
 * doku-dimension. Bewusst getrennt vom alten green/yellow/red-naming, damit
 * im UI nie „rot = kaputt" suggeriert wird — eine doku-lücke ist ein
 * organisatorischer hinweis, kein lizenz-defekt.
 *
 *   green  → complete
 *   yellow → hint   (muss-stufe ohne genug doku)
 *   red    → gap    (kritische exposure ohne genug doku)
 */
export function childDocState(child: TraegerChildSummary): ChildDocState {
  switch (child.compliance_status) {
    case "green":
      return "complete";
    case "yellow":
      return "hint";
    case "red":
    default:
      return "gap";
  }
}

// ─── Render: status-chip ─────────────────────────────────────────────────────

function chipVisual(
  kind: "license" | "doc",
  state: ChildLicenseState | ChildDocState,
): StatusVisual {
  return kind === "license"
    ? LICENSE_VISUAL[state as ChildLicenseState]
    : DOC_VISUAL[state as ChildDocState];
}

/**
 * StatusChip — ein einzelner status-chip, color-blind-safe (icon + text +
 * farbe). `size="sm"` für dichte grids (Vergleich), default für dashboard.
 */
export function StatusChip({
  kind,
  state,
  size = "md",
}: {
  kind: "license" | "doc";
  state: ChildLicenseState | ChildDocState;
  size?: "sm" | "md";
}): React.ReactElement {
  const v = chipVisual(kind, state);
  const Icon = v.Icon;
  const pad = size === "sm" ? "px-2 py-0.5 text-xs gap-1" : "px-2.5 py-1 text-sm gap-1.5";
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${pad} ${v.classes}`}
    >
      <Icon aria-hidden="true" className={size === "sm" ? "size-3.5" : "size-4"} />
      <span>{v.label}</span>
      {/* zusätzlicher kontext nur für screen-reader, falls label allein knapp */}
      {v.srHint !== v.label && <span className="sr-only"> — {v.srHint}</span>}
    </span>
  );
}

/**
 * ChildStatusChips — die zwei orthogonalen dimensionen als gruppe.
 * group-role + label macht klar, dass es ZWEI getrennte status sind.
 */
export function ChildStatusChips({
  child,
  size = "md",
}: {
  child: TraegerChildSummary;
  size?: "sm" | "md";
}): React.ReactElement {
  return (
    <div
      role="group"
      aria-label={`Status ${child.org_name}: Lizenz und Dokumentation`}
      className="flex flex-wrap items-center gap-1.5"
    >
      <StatusChip kind="license" state={childLicenseState(child)} size={size} />
      <StatusChip kind="doc" state={childDocState(child)} size={size} />
    </div>
  );
}

// ─── Render: lizenz-verlängern-CTA (komplementär zu B10 ReadOnlyBanner) ──────

/**
 * mailto-CTA pro Einrichtung. Komplementär zum globalen B10-ReadOnlyBanner
 * (der nur die eigene träger-lizenz adressiert) — hier per-child, mit
 * org-name im betreff, damit der vertrieb die richtige einrichtung zuordnet.
 *
 * Y1: mailto (manueller checkout). Y2: self-service-checkout-route.
 * Touch-target ≥56px (var(--size-touch-primary)) für lese-brillen-zielgruppe.
 */
export function renewLicenseHref(child: TraegerChildSummary): string {
  const subject = `Lizenz verlängern — ${child.org_name}`;
  const body =
    `Wir möchten die HitzeProtokoll-Lizenz für die Einrichtung ` +
    `„${child.org_name}" (ID: ${child.org_id}) verlängern bzw. reaktivieren.`;
  return (
    "mailto:hello@hitzeprotokoll.de" +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

export function RenewLicenseCTA({
  child,
  className = "",
}: {
  child: TraegerChildSummary;
  className?: string;
}): React.ReactElement {
  return (
    <a
      href={renewLicenseHref(child)}
      className={
        "inline-flex min-h-[var(--size-touch-primary)] items-center justify-center gap-1.5 " +
        "rounded-md bg-[color:var(--color-brand-primary-strong)] px-4 text-sm font-semibold " +
        "text-white transition-colors hover:bg-[color:var(--color-warm-orange-800)] " +
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 " +
        "focus-visible:outline-[color:var(--color-border-focus)] " +
        className
      }
    >
      <CalendarClock aria-hidden="true" className="size-4" />
      Lizenz verlängern
    </a>
  );
}
